import React, { lazy, Suspense } from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { AuthProvider } from './contexts/AuthContext';
import { CampProvider } from './contexts/CampContext';
import './App.css';

// ── Layouts ───────────────────────────────────────────────────────────────────
import PlatformLayout from './layouts/PlatformLayout';
import CampPublicLayout from './layouts/CampPublicLayout';
import CampInternalLayout from './layouts/CampInternalLayout';
import SuperAdminLayout from './layouts/SuperAdminLayout';
import PermissionGuard from './components/PermissionGuard';

// ── Platform pages ────────────────────────────────────────────────────────────
const PlatformLandingPage = lazy(() => import('./pages/PlatformLandingPage'));

// ── SuperAdmin pages ──────────────────────────────────────────────────────────
const SADashboard = lazy(() => import('./pages/admin/SADashboard'));
const SACampsPage = lazy(() => import('./pages/admin/SACampsPage'));
const SAUsersPage = lazy(() => import('./pages/admin/SAUsersPage'));
const SAEquipmentPage = lazy(() => import('./pages/admin/SAEquipmentPage'));
const SASettingsPage = lazy(() => import('./pages/admin/SASettingsPage'));
const SABackupsPage = lazy(() => import('./pages/admin/SABackupsPage'));
const SATemplatesPage = lazy(() => import('./pages/admin/SATemplatesPage'));
const SATenantsPage = lazy(() => import('./pages/admin/SATenantsPage'));
const SALoginPage = lazy(() => import('./pages/admin/SALoginPage'));

// ── Camp public pages ─────────────────────────────────────────────────────────
const CampLandingPage = lazy(() => import('./pages/camp/CampLandingPage'));
const CampLoginPage = lazy(() => import('./pages/camp/CampLoginPage'));
const CampRegisterPage = lazy(() => import('./pages/camp/CampRegisterPage'));
const CampNewsPage = lazy(() => import('./pages/camp/CampNewsPage'));
const CampNewsDetailPage = lazy(() => import('./pages/camp/CampNewsDetailPage'));
const CampTeamsPage = lazy(() => import('./pages/camp/CampTeamsPage'));
const CampTeamDetailPage = lazy(() => import('./pages/camp/CampTeamDetailPage'));
const CampTournamentPublicPage = lazy(() => import('./pages/camp/CampTournamentPublicPage'));
const CampRegistrationPage = lazy(() => import('./pages/camp/CampRegistrationPage'));
const CampDownloadsPage = lazy(() => import('./pages/camp/CampDownloadsPage'));
const CampEveningsPage = lazy(() => import('./pages/camp/CampEveningsPage'));

// ── Camp internal pages ───────────────────────────────────────────────────────
const InternalDashboard = lazy(() => import('./pages/camp/intern/InternalDashboard'));
const ParticipantsPage = lazy(() => import('./pages/camp/intern/ParticipantsPage'));
const TeamsManagementPage = lazy(() => import('./pages/camp/intern/TeamsManagementPage'));
const TournamentPage = lazy(() => import('./pages/camp/intern/TournamentPage'));
const SchedulePage = lazy(() => import('./pages/camp/intern/SchedulePage'));
const EveningsPage = lazy(() => import('./pages/camp/intern/EveningsPage'));
const TeamtimesPage = lazy(() => import('./pages/camp/intern/TeamtimesPage'));
const ListsPage = lazy(() => import('./pages/camp/intern/ListsPage'));
const FilesPage = lazy(() => import('./pages/camp/intern/FilesPage'));
const TodosPage = lazy(() => import('./pages/camp/intern/TodosPage'));
const MealsPage = lazy(() => import('./pages/camp/intern/MealsPage'));
const FinancePage = lazy(() => import('./pages/camp/intern/FinancePage'));
const StaffPage = lazy(() => import('./pages/camp/intern/StaffPage'));
const EquipmentPage = lazy(() => import('./pages/camp/intern/EquipmentPage'));
const CheckinPage = lazy(() => import('./pages/camp/intern/CheckinPage'));
const QuestionsPage = lazy(() => import('./pages/camp/intern/QuestionsPage'));
const CampAdminPage = lazy(() => import('./pages/camp/intern/CampAdminPage'));
const AttendancePage = lazy(() => import('./pages/camp/intern/AttendancePage'));
const FormsBuilderPage = lazy(() => import('./pages/camp/intern/FormsBuilderPage'));
const DownloadsAdminPage = lazy(() => import('./pages/camp/intern/DownloadsAdminPage'));
const StatisticsPage = lazy(() => import('./pages/camp/intern/StatisticsPage'));
const NewsAdminPage = lazy(() => import('./pages/camp/intern/NewsAdminPage'));
const EmailNotificationsPage = lazy(() => import('./pages/camp/intern/EmailNotificationsPage'));

const Spinner = () => (
  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '60vh' }}>
    <div className="spinner" />
  </div>
);

// Auto-reload on ChunkLoadError: happens when a new deployment replaces JS chunks
// that the browser still references from a cached index.html.
if (typeof window !== 'undefined') {
  window.addEventListener('unhandledrejection', (event) => {
    if (event.reason?.name === 'ChunkLoadError') {
      window.location.reload();
    }
  });
}

export default function App() {
  return (
    <AuthProvider>
      <BrowserRouter>
        <Suspense fallback={<Spinner />}>
          <Routes>
            {/* ── Platform landing ─────────────────────────────── */}
            <Route element={<PlatformLayout />}>
              <Route index element={<PlatformLandingPage />} />
            </Route>

            {/* ── SuperAdmin area ──────────────────────────────── */}
            <Route path="/admin/login" element={<SALoginPage />} />
            <Route path="/admin" element={<SuperAdminLayout />}>
              <Route index element={<Navigate to="/admin/dashboard" replace />} />
              <Route path="dashboard" element={<SADashboard />} />
              <Route path="camps" element={<SACampsPage />} />
              <Route path="tenants" element={<SATenantsPage />} />
              <Route path="users" element={<SAUsersPage />} />
              <Route path="equipment" element={<SAEquipmentPage />} />
              <Route path="templates" element={<SATemplatesPage />} />
              <Route path="settings" element={<SASettingsPage />} />
              <Route path="backups" element={<SABackupsPage />} />
            </Route>

            {/* ── Camp routes ──────────────────────────────────── */}
            <Route path="/:campSlug" element={<CampProvider><CampPublicLayout /></CampProvider>}>
              <Route index element={<CampLandingPage />} />
              <Route path="news" element={<CampNewsPage />} />
              <Route path="news/:slug" element={<CampNewsDetailPage />} />
              <Route path="teams" element={<CampTeamsPage />} />
              <Route path="teams/:id" element={<CampTeamDetailPage />} />
              <Route path="turnier" element={<CampTournamentPublicPage />} />
              <Route path="anmeldung" element={<CampRegistrationPage />} />
              <Route path="anmeldung/:formId" element={<CampRegistrationPage />} />
              <Route path="downloads" element={<CampDownloadsPage />} />
              <Route path="abende" element={<CampEveningsPage />} />
            </Route>

            <Route path="/:campSlug/login" element={<CampProvider><CampLoginPage /></CampProvider>} />
            <Route path="/:campSlug/registrieren" element={<CampProvider><CampRegisterPage /></CampProvider>} />

            {/* ── Camp internal ────────────────────────────────── */}
            <Route path="/:campSlug/intern" element={<CampProvider><CampInternalLayout /></CampProvider>}>
              <Route index element={<Navigate to="dashboard" replace />} />
              <Route path="dashboard" element={<InternalDashboard />} />
              <Route path="teilnehmer" element={<PermissionGuard require="participants.read"><ParticipantsPage /></PermissionGuard>} />
              <Route path="teams" element={<PermissionGuard require="teams.read"><TeamsManagementPage /></PermissionGuard>} />
              <Route path="turnier" element={<PermissionGuard require="tournament.read"><TournamentPage /></PermissionGuard>} />
              <Route path="plan" element={<PermissionGuard require="schedule.read"><SchedulePage /></PermissionGuard>} />
              <Route path="abende" element={<PermissionGuard require="evenings.read"><EveningsPage /></PermissionGuard>} />
              <Route path="teamtimes" element={<PermissionGuard require="teamtimes.read"><TeamtimesPage /></PermissionGuard>} />
              <Route path="listen" element={<PermissionGuard require="lists.read"><ListsPage /></PermissionGuard>} />
              <Route path="dateien" element={<PermissionGuard require="files.read"><FilesPage /></PermissionGuard>} />
              <Route path="todos" element={<PermissionGuard require="todos.read"><TodosPage /></PermissionGuard>} />
              <Route path="kueche" element={<PermissionGuard require="meals.read"><MealsPage /></PermissionGuard>} />
              <Route path="finanzen" element={<PermissionGuard require="finance.read"><FinancePage /></PermissionGuard>} />
              <Route path="mitarbeiter" element={<PermissionGuard require="staff.read"><StaffPage /></PermissionGuard>} />
              <Route path="equipment" element={<PermissionGuard require="equipment.read"><EquipmentPage /></PermissionGuard>} />
              <Route path="checkin" element={<PermissionGuard require="checkin.read"><CheckinPage /></PermissionGuard>} />
              <Route path="frage" element={<PermissionGuard require="questions.read"><QuestionsPage /></PermissionGuard>} />
              <Route path="anwesenheit" element={<PermissionGuard require="attendance.read"><AttendancePage /></PermissionGuard>} />
              <Route path="formulare" element={<PermissionGuard require="registration.read"><FormsBuilderPage /></PermissionGuard>} />
              <Route path="downloads" element={<PermissionGuard require="flyer.read"><DownloadsAdminPage /></PermissionGuard>} />
              <Route path="statistiken" element={<PermissionGuard require="stats.read"><StatisticsPage /></PermissionGuard>} />
              <Route path="neuigkeiten" element={<PermissionGuard require="news.read"><NewsAdminPage /></PermissionGuard>} />
              <Route path="emails" element={<PermissionGuard require="news.read"><EmailNotificationsPage /></PermissionGuard>} />
              <Route path="admin" element={<PermissionGuard require="admin.settings"><CampAdminPage /></PermissionGuard>} />
            </Route>
          </Routes>
        </Suspense>
      </BrowserRouter>
    </AuthProvider>
  );
}
